import React, { useEffect, useRef, useState } from 'react';
import { func, object, shape } from 'prop-types';
import { Field } from 'react-final-form';

import { FormattedMessage, intlShape } from '../../util/reactIntl';

import Button, { SecondaryButton } from '../Button/Button';
import IconSpinner from '../IconSpinner/IconSpinner';
import AspectRatioWrapper from '../AspectRatioWrapper/AspectRatioWrapper';

import css from './DniPhotoFields.module.css';

const FRONT = 'dni_front';
const BACK = 'dni_back';

// Con qué nombre se guardan en Sharetribe. Es lo único que ve el operador en la
// Consola, así que tiene que decir qué cara es sin abrir el fichero.
const PHOTO_NAMES = { [FRONT]: 'dni-anverso', [BACK]: 'dni-reverso' };

// Se conserva la extensión original para no romper el tipo que enseña la
// Consola; si el fichero no trae ninguna, se deduce del MIME.
export const storedNameFor = (fieldName, uploaded) => {
  const fromName = (uploaded.name || '').match(/\.[a-z0-9]+$/i);
  const fromMime = (uploaded.type || '').split('/')[1];
  const extension = fromName ? fromName[0].toLowerCase() : fromMime ? `.${fromMime}` : '';
  return `${PHOTO_NAMES[fieldName]}${extension}`;
};

/**
 * Las dos caras del DNI, como ficheros privados de Sharetribe.
 *
 * Lo que se guarda en el campo —y por tanto en `privateData`— es el **id** del
 * fichero, no una URL. Antes esto subía a S3 con una URL prefirmada y
 * `ACL: 'public-read'`, así que cada documento de identidad quedaba en una URL
 * pública y permanente; el bucket, además, no era nuestro. Ahora el control de
 * acceso lo pone Sharetribe: solo el dueño y el operador.
 *
 * Colgarlas del perfil es cosa de quien envía el formulario, vía
 * `privateFileAttachments` — ver BillingDetailsPage.
 *
 * El formulario que lo use aporta `form` y `values` de react-final-form, y las
 * dos funciones que hablan con el SDK.
 */
const DniPhotoFields = props => {
  const { form, values, intl, onUploadFile, onResolveFileUrl, onPhotosChanged } = props;

  const [uploading, setUploading] = useState({});
  const [previews, setPreviews] = useState({});
  const [failed, setFailed] = useState(null);
  const inputRefs = { [FRONT]: useRef(null), [BACK]: useRef(null) };

  // Se lee dentro del efecto sin declararlo como dependencia: si `previews`
  // entrara en el array, cada resolución dispararía el efecto otra vez.
  const previewsRef = useRef(previews);
  previewsRef.current = previews;

  const frontId = values?.[FRONT] || '';
  const backId = values?.[BACK] || '';

  // Las URLs de descarga son de vida corta a propósito, así que no se guardan en
  // ningún sitio: se piden cuando aparece un id que todavía no tiene vista previa.
  useEffect(() => {
    let cancelled = false;

    [frontId, backId]
      .filter(id => id && !previewsRef.current[id])
      .forEach(id => {
        onResolveFileUrl(id)
          .then(url => {
            if (!cancelled) {
              setPreviews(prev => ({ ...prev, [id]: url }));
            }
          })
          .catch(() => {
            // Sin URL no hay miniatura; el campo conserva el id y el resto del
            // formulario sigue siendo utilizable.
          });
      });

    return () => {
      cancelled = true;
    };
  }, [frontId, backId, onResolveFileUrl]);

  // `privateFileAttachments` reemplaza la lista entera, así que cualquier
  // cambio viaja con las dos caras. El par se calcula aquí, en el único sitio
  // que sabe cuál acaba de cambiar.
  const persist = (fieldName, fileId) =>
    onPhotosChanged({ [FRONT]: frontId, [BACK]: backId, [fieldName]: fileId });

  const remove = fieldName => {
    form.change(fieldName, '');
    persist(fieldName, '');
  };

  const upload = (e, fieldName) => {
    const selected = e.target.files && e.target.files[0];
    if (!selected) {
      return;
    }

    setFailed(null);
    setUploading(prev => ({ ...prev, [fieldName]: true }));

    // La miniatura sale del fichero local, así que no hace falta ir a buscar una
    // URL de descarga para lo que el usuario acaba de elegir.
    const localUrl =
      typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(selected) : null;

    onUploadFile(selected, storedNameFor(fieldName, selected))
      .then(fileId => {
        if (localUrl) {
          setPreviews(prev => ({ ...prev, [fileId]: localUrl }));
        }
        form.change(fieldName, fileId);
        // Se guarda al momento: obligar a pulsar "Guardar cambios" dejaba el
        // fichero subido pero sin colgar del perfil, es decir, huérfano.
        persist(fieldName, fileId);
      })
      .catch(() => {
        if (localUrl && URL.revokeObjectURL) {
          URL.revokeObjectURL(localUrl);
        }
        // Que falle en silencio era el problema anterior: el spinner se apagaba
        // y no pasaba nada más, así que no había forma de saberlo.
        setFailed(fieldName);
      })
      .then(() => setUploading(prev => ({ ...prev, [fieldName]: false })));
  };

  const side = (fieldName, labelId, altId) => {
    const fileId = values?.[fieldName] || '';
    const previewUrl = previews[fileId];
    const isUploading = !!uploading[fieldName];

    return (
      <div className={css.imageUploaderBox}>
        <label>
          <FormattedMessage id={labelId} />
        </label>
        {/*
        Registra el campo en final-form sin pintar nada. Sin esto, `form.change`
        no ensucia el formulario: `pristine` se calcula SOLO sobre los campos
        registrados (final-form, `safeFieldKeys`), y las dos caras se manejan con
        <input type="file"> crudos, no con <Field>. El botón de guardar se queda
        deshabilitado, no hay `update_profile`, y el fichero subido se queda
        huérfano: nunca llega a colgarse del perfil.
      */}
        <Field name={fieldName} render={() => null} />
        <div className={css.addImageWrapper}>
          {fileId ? (
            <div className={css.frontDniWrapper}>
              {isUploading || !previewUrl ? (
                <div className={css.uploadingImageOverlay}>
                  <IconSpinner />
                </div>
              ) : (
                <div className={css.imageShown}>
                  <img
                    src={previewUrl}
                    height={'230px'}
                    width={'290px'}
                    alt={intl.formatMessage({ id: altId })}
                  />
                  <div className={css.bottomButton}>
                    <Button type={'button'} onClick={() => inputRefs[fieldName].current.click()}>
                      <FormattedMessage id="ContactDetailsForm.uploadNewButton" />
                    </Button>
                    <SecondaryButton onClick={() => remove(fieldName)}>
                      <FormattedMessage id="ContactDetailsForm.deleteButton" />
                    </SecondaryButton>
                  </div>
                </div>
              )}
              <input
                className={css.addImageInput}
                id={fieldName}
                name={fieldName}
                type="file"
                accept="image/*"
                onChange={e => upload(e, fieldName)}
                ref={inputRefs[fieldName]}
              />
            </div>
          ) : (
            <AspectRatioWrapper width={1} height={1}>
              <input
                className={css.addImageInput}
                id={fieldName}
                name={fieldName}
                type="file"
                accept="image/*"
                onChange={e => upload(e, fieldName)}
                ref={inputRefs[fieldName]}
              />
              <label htmlFor={fieldName} className={css.dniAddImage}>
                {isUploading ? (
                  <IconSpinner />
                ) : (
                  <span className={css.chooseImageText}>
                    <span className={css.chooseImage}>
                      <FormattedMessage id="EditListingPhotosForm.chooseImage" />
                    </span>
                    <span className={css.imageTypes}>
                      <FormattedMessage id="EditListingPhotosForm.imageTypes" />
                    </span>
                  </span>
                )}
              </label>
            </AspectRatioWrapper>
          )}
        </div>
        {failed === fieldName ? (
          <p className={css.uploadError}>
            <FormattedMessage id="DniPhotoFields.uploadFailed" />
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <div className={css.imagesUpload}>
      <label>
        <FormattedMessage id="ContactDetailsForm.dniLabel" />
      </label>
      <div className={css.inputRow}>
        {side(FRONT, 'ContactDetailsForm.dniFront', 'ContactDetailsForm.dniFrontFaceAlt')}
        {side(BACK, 'ContactDetailsForm.dniBack', 'ContactDetailsForm.dniBackFaceAlt')}
      </div>
    </div>
  );
};

DniPhotoFields.defaultProps = { values: {} };

DniPhotoFields.propTypes = {
  form: shape({ change: func.isRequired }).isRequired,
  values: object,
  intl: intlShape.isRequired,
  // Sube el fichero con el nombre dado y resuelve con su id.
  // Ver ProfileSettingsPage.duck.js.
  onUploadFile: func.isRequired,
  // Resuelve con una URL de lectura, de vida corta, para un id ya guardado.
  onResolveFileUrl: func.isRequired,
  // Persiste el par completo de caras en cuanto una de las dos cambia.
  onPhotosChanged: func.isRequired,
};

export default DniPhotoFields;
